'use client';

import { useMemo } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { AnimatedValue } from '@/components/animated-value';
import { chartColor } from '@/lib/chart-palette';
import { formatPercent } from '@/lib/format/percent/percent';

export interface PortfolioDonutSegment {
  id: string;
  label: string;
  /** Popover caption, e.g. "ROWA vault · Sepolia". */
  detail?: string;
  valueLabel: string;
  /** Percent of the reconciled total, 0–100. */
  share: number;
  /** Extra popover fact rows, e.g. price source or vault count. */
  facts?: { label: string; value: string }[];
  /**
   * Members nested inside this segment (a sunburst's outer ring). Children
   * shares are of the same total as their parents, so the outer ring lays
   * out in parent order and stays angularly aligned with the inner ring.
   */
  children?: PortfolioDonutSegment[];
}

interface PortfolioDonutProps {
  ariaLabel: string;
  segments: PortfolioDonutSegment[];
  centerValue: string;
  centerLabel: string;
  centerSub?: string;
  emptyLabel?: string;
  /** Eyebrow inside the hover popover, e.g. "OWNED POSITION". */
  popoverEyebrow?: string;
  /**
   * What a segment's share is a share of, when it is not the whole, e.g.
   * "of valued holdings". Printed after the percentage in the popover.
   */
  shareLabel?: string;
  /** Shared hover scope — the ledger drives the same ids. */
  previewId: string | null;
  onPreview: (id: string | null) => void;
}

/**
 * Outer-ring styling: members take their own hues from the theme's chart
 * ramp, continued past the buckets so the two rings never start on the
 * same color — one dominant bucket no longer washes the whole chart in a
 * single family. A soft opacity alternation separates neighbors once the
 * seven-token ramp wraps. Exported so ledger rows wear exactly their arc.
 */
export function memberArcStyles(
  segments: PortfolioDonutSegment[]
): Map<string, { color: string; opacity: number }> {
  const map = new Map<string, { color: string; opacity: number }>();
  let memberIndex = segments.length;
  for (const segment of segments) {
    for (const child of segment.children ?? []) {
      map.set(child.id, {
        color: chartColor(memberIndex),
        opacity: memberIndex % 2 === 0 ? 1 : 0.78
      });
      memberIndex += 1;
    }
  }
  return map;
}

const GAP = 2.5;
/** Single-ring geometry (flat lens). */
const SOLO_RADIUS = 72;
const SOLO_WIDTH = 24;
/** Sunburst geometry: buckets inside, their members outside. */
const INNER_RADIUS = 54;
const INNER_WIDTH = 20;
const OUTER_RADIUS = 79;
const OUTER_WIDTH = 13;

interface Arc {
  segment: PortfolioDonutSegment;
  radius: number;
  width: number;
  color: string;
  /** Softens member arcs so the inner ring reads as the primary layer. */
  baseOpacity: number;
  parentId: string | null;
}

/**
 * Decision-useful composition chart (PO2, node 1787:799). Flat segment
 * lists render one ring; segments with children render a two-ring sunburst
 * whose outer arcs nest inside their parent's span — the same
 * bucket-then-members hierarchy as the paired ledger. Pointer hover
 * previews either layer in sync with the table; the semantic ledger
 * remains the accessible record — the chart itself is presentational.
 */
export function PortfolioDonut({
  ariaLabel,
  segments,
  centerValue,
  centerLabel,
  centerSub,
  emptyLabel = 'No priced value',
  popoverEyebrow = 'Selected segment',
  shareLabel,
  previewId,
  onPreview
}: PortfolioDonutProps) {
  const reducedMotion = useReducedMotion();
  const activeId = previewId;

  const hasChildren = segments.some(
    (segment) => (segment.children?.length ?? 0) > 0
  );

  const totalShare = useMemo(
    () =>
      segments.reduce((sum, segment) => sum + Math.max(segment.share, 0), 0),
    [segments]
  );

  const { arcs, byId, parentOf } = useMemo(() => {
    const list: Arc[] = [];
    const lookup = new Map<string, PortfolioDonutSegment>();
    const parents = new Map<string, string>();
    const childStyles = memberArcStyles(segments);

    segments.forEach((segment, index) => {
      lookup.set(segment.id, segment);
      list.push({
        segment,
        radius: hasChildren ? INNER_RADIUS : SOLO_RADIUS,
        width: hasChildren ? INNER_WIDTH : SOLO_WIDTH,
        color: chartColor(index),
        baseOpacity: 1,
        parentId: null
      });
      segment.children?.forEach((child) => {
        lookup.set(child.id, child);
        parents.set(child.id, segment.id);
        const style = childStyles.get(child.id)!;
        list.push({
          segment: child,
          radius: OUTER_RADIUS,
          width: OUTER_WIDTH,
          color: style.color,
          baseOpacity: style.opacity,
          parentId: segment.id
        });
      });
    });

    return { arcs: list, byId: lookup, parentOf: parents };
  }, [segments, hasChildren]);

  const activeSegment = activeId ? byId.get(activeId) : undefined;

  // Per-ring angular offsets, both driven by the same total so the outer
  // ring's members stay under their parent bucket.
  const ringOffsets = new Map<number, number>();

  return (
    <div className="relative mx-auto w-full max-w-[26rem]">
      <div className="relative">
        <svg
          viewBox="0 0 200 200"
          className="mx-auto aspect-square w-full max-w-[24rem] overflow-visible"
          role="group"
          aria-label={ariaLabel}
        >
          <title>{ariaLabel}</title>
          <circle
            cx="100"
            cy="100"
            r={hasChildren ? INNER_RADIUS : SOLO_RADIUS}
            fill="none"
            stroke="var(--color-rowa-border-subtle)"
            strokeWidth={hasChildren ? INNER_WIDTH : SOLO_WIDTH}
            aria-hidden
          />
          {hasChildren ? (
            <circle
              cx="100"
              cy="100"
              r={OUTER_RADIUS}
              fill="none"
              stroke="var(--color-rowa-border-subtle)"
              strokeWidth={OUTER_WIDTH}
              opacity={0.6}
              aria-hidden
            />
          ) : null}

          {totalShare > 0 ? (
            <motion.g
              initial={
                reducedMotion ? false : { opacity: 0, rotate: -18, scale: 0.96 }
              }
              animate={{ opacity: 1, rotate: 0, scale: 1 }}
              transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
              style={{ transformOrigin: '100px 100px' }}
            >
              {arcs.map((arc) => {
                const circumference = 2 * Math.PI * arc.radius;
                const normalizedShare =
                  Math.max(arc.segment.share, 0) / totalShare;
                const length = normalizedShare * circumference;
                const dashLength = Math.max(length - GAP, 0);
                const offset = ringOffsets.get(arc.radius) ?? 0;
                ringOffsets.set(arc.radius, offset + length);

                const isActive = arc.segment.id === activeId;
                // A hovered bucket keeps its members lit and the other way
                // round — only unrelated arcs recede.
                const isRelated =
                  activeId != null &&
                  (parentOf.get(activeId) === arc.segment.id ||
                    arc.parentId === activeId);
                const isDimmed = activeId != null && !isActive && !isRelated;

                return (
                  <circle
                    key={`${arc.radius}:${arc.segment.id}`}
                    cx="100"
                    cy="100"
                    r={arc.radius}
                    fill="none"
                    stroke={arc.color}
                    strokeWidth={isActive ? arc.width + 4 : arc.width}
                    strokeDasharray={`${dashLength} ${circumference - dashLength}`}
                    strokeDashoffset={-offset}
                    transform="rotate(-90 100 100)"
                    style={{
                      opacity: arc.baseOpacity * (isDimmed ? 0.35 : 1)
                    }}
                    className="transition-[stroke-width,stroke-dasharray,stroke-dashoffset,opacity] duration-500"
                    aria-hidden
                    onMouseEnter={() => onPreview(arc.segment.id)}
                    onMouseLeave={() => onPreview(null)}
                  />
                );
              })}
            </motion.g>
          ) : null}
        </svg>

        <div
          className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center text-center"
          aria-hidden
        >
          {totalShare > 0 ? (
            <>
              <AnimatedValue
                value={centerValue}
                className="text-rowa-data-large text-rowa-text-primary"
              />
              <span className="text-rowa-label-sm text-rowa-text-secondary mt-1 tracking-[0.08em] uppercase">
                {centerLabel}
              </span>
              {centerSub ? (
                <span className="text-rowa-label-sm text-rowa-text-secondary mt-0.5">
                  {centerSub}
                </span>
              ) : null}
            </>
          ) : (
            <>
              <span className="text-rowa-data-large text-rowa-text-tertiary">
                —
              </span>
              <span className="text-rowa-label-sm text-rowa-text-tertiary mt-1">
                {emptyLabel}
              </span>
            </>
          )}
        </div>

        <AnimatePresence>
          {activeSegment ? (
            <motion.div
              key={activeSegment.id}
              initial={
                reducedMotion ? false : { opacity: 0, y: 8, scale: 0.98 }
              }
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={
                reducedMotion ? undefined : { opacity: 0, y: 4, scale: 0.98 }
              }
              transition={{ duration: 0.2, ease: 'easeOut' }}
              className="pointer-events-none absolute top-1 left-1/2 z-10 w-72 max-w-full -translate-x-1/2"
            >
              {/* Shared overlay recipe (wallet menu, network menu, popovers):
                  frosted enough that donut pixels never collide with text. */}
              <div
                className="border-rowa-border-glass-highlight bg-rowa-surface-overlay shadow-rowa-panel rounded-rowa-lg backdrop-blur-rowa-overlay border p-5"
                role="status"
                aria-live="polite"
              >
                <p className="text-rowa-label-sm text-rowa-text-brand tracking-[0.08em] uppercase">
                  {popoverEyebrow}
                </p>
                <p className="text-rowa-data-medium text-rowa-text-primary mt-2">
                  {activeSegment.label}
                </p>
                <p className="text-rowa-data-large text-rowa-text-primary mt-1 tabular-nums">
                  {activeSegment.valueLabel} ·{' '}
                  {`${formatPercent(activeSegment.share, { digits: 1 })}${shareLabel ? ` ${shareLabel}` : ''}`}
                </p>
                {activeSegment.detail ? (
                  <p className="text-rowa-label-sm text-rowa-text-secondary mt-1.5">
                    {activeSegment.detail}
                  </p>
                ) : null}
                {activeSegment.facts && activeSegment.facts.length > 0 ? (
                  <dl className="border-rowa-border-subtle mt-3 space-y-1.5 border-t pt-3">
                    {activeSegment.facts.map((fact) => (
                      <div
                        key={fact.label}
                        className="flex items-center justify-between gap-3"
                      >
                        <dt className="text-rowa-label-sm text-rowa-text-secondary">
                          {fact.label}
                        </dt>
                        <dd className="text-rowa-label-sm text-rowa-text-primary text-right font-medium">
                          {fact.value}
                        </dd>
                      </div>
                    ))}
                  </dl>
                ) : null}
              </div>
            </motion.div>
          ) : null}
        </AnimatePresence>
      </div>
    </div>
  );
}
